import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import {
  Plus,
  Edit,
  Trash2,
  Copy,
  Save,
  X,
  Settings,
  FileText,
  CheckCircle2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Info,
  Target,
  AlertCircle,
  AlertTriangle,
  CheckSquare,
  Clock,
  Tag,
  List,
  GripVertical
} from "lucide-react";
import { BlocosPerguntasTab } from './BlocosPerguntasTab';
import { TemplateForm } from './TemplateForm';
import {
  Template,
  PerguntaFormulario,
  RegraNavegacao,
  BlocoPerguntas,
  etapasFunil,
  tiposTarefa,
  prioridades,
  ConclusaoAutomatica,
  LINHA_PRODUTO_TEMPLATE_LABELS,
} from '@/types/tarefas';

interface GerenciarTemplatesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GerenciarTemplatesModal({ open, onOpenChange }: GerenciarTemplatesModalProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [filterEtapa, setFilterEtapa] = useState<string>('all');
  const [filterLinha, setFilterLinha] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<string>('titulo');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [activeTab, setActiveTab] = useState<string>('list');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState<(() => void) | null>(null);
  const [blocosDisponiveis, setBlocosDisponiveis] = useState<BlocoPerguntas[]>([]);
  const { toast } = useToast();

  // Estado para rastrear dados originais do template (para detectar mudanças não salvas)
  const [originalFormData, setOriginalFormData] = useState<Partial<Template> | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<Template>>({
    titulo: '',
    objetivo: '',
    descricao: '',
    instrucoes: '',
    etapa_funil: '',
    linha_produto: 'fotovoltaico',
    tipo_tarefa: 'acao',
    prazo_horas: undefined,
    prioridade: 'media',
    ativo: true,
    ordem: 1,
    perguntas_spin: [],
    perguntas_formulario: []
  });



  // Função para limpar vínculos inválidos em perguntas existentes
  const limparVinculosInvalidos = (perguntas: PerguntaFormulario[]): PerguntaFormulario[] => {
    if (!perguntas || perguntas.length === 0) return perguntas;

    // Criar um conjunto de IDs válidos de perguntas
    const idsValidos = new Set(perguntas.map(p => p.id));

    return perguntas.map((pergunta) => {
      const perguntaAtualizada = { ...pergunta };

      // Limpar proxima_pergunta_id se apontar para pergunta inexistente
      if (pergunta.proxima_pergunta_id && !idsValidos.has(pergunta.proxima_pergunta_id)) {
        perguntaAtualizada.proxima_pergunta_id = undefined;
      }

      // Limpar regras de navegação que apontam para perguntas inexistentes
      // IMPORTANTE: Preservar regras que apontam para blocos (proximo_bloco_id)
      if (Array.isArray(pergunta.regras_navegacao)) {
        perguntaAtualizada.regras_navegacao = pergunta.regras_navegacao
          .filter(regra => {
            // Se tem proximo_bloco_id, sempre manter (blocos são válidos)
            if (regra.proximo_bloco_id) {
              return true;
            }
            // Se tem proxima_pergunta_id, verificar se a pergunta existe
            if (regra.proxima_pergunta_id) {
              return idsValidos.has(regra.proxima_pergunta_id);
            }
            // Se não tem destino, manter (pode ser regra de fim)
            return true;
          })
          .map(regra => ({ ...regra })); // Criar cópia para evitar mutação
      }

      return perguntaAtualizada;
    });
  };

  useEffect(() => {
    if (open) {
      fetchTemplates();
      fetchBlocos();
    }
  }, [open, filterEtapa]);

  const fetchBlocos = async () => {
    try {
      const response = await fetchWithAuth('/api/blocos-perguntas');
      if (response.ok) {
        const data: BlocoPerguntas[] = await response.json();
        setBlocosDisponiveis(data || []);
      }
    } catch (error) {
      console.error('Erro ao buscar blocos:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterEtapa !== 'all') {
        params.append('etapa_funil', filterEtapa);
      }
      params.append('ativo', 'true');

      const queryString = params.toString();
      const url = queryString ? `/api/tarefas-templates?${queryString}` : '/api/tarefas-templates';
      
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        throw new Error('Erro ao buscar templates');
      }

      const data: Template[] = await response.json();
      
      // Função helper para verificar se todas as opções estão marcadas para encerrar
      const todasOpcoesEncerram = (opcoes: string[], regras: any[]): boolean => {
        const opcoesValidas = opcoes.map((opcao) => opcao.trim()).filter((opcao) => opcao !== '');
        if (opcoesValidas.length === 0) {
          return false;
        }
        
        // Verificar se todas as opções válidas têm regras que encerram (sem proxima_pergunta_id e sem proximo_bloco_id)
        return opcoesValidas.every((resposta) => {
          const regra = regras.find((r) => r.resposta === resposta);
          return regra && !regra.proxima_pergunta_id && !regra.proximo_bloco_id;
        });
      };

      // Normalizar perguntas_formulario para preservar arrays vazios que indicam fluxo condicional
      const templatesNormalizados = data.map((template) => {
        const templateNormalizado = {
          ...template,
          perguntas_formulario: template.perguntas_formulario?.map((p) => {
            
            // Normalizar regras de navegação preservando proximo_bloco_id ao carregar do banco
            let regrasNormalizadas = undefined;
            if (p.regras_navegacao !== undefined && p.regras_navegacao !== null) {
              if (Array.isArray(p.regras_navegacao)) {
                regrasNormalizadas = p.regras_navegacao.map(regra => {
                  const regraNormalizada: any = {
                    resposta: regra.resposta || ''
                  };
                  // Preservar proxima_pergunta_id se existir
                  if (regra.proxima_pergunta_id) {
                    regraNormalizada.proxima_pergunta_id = regra.proxima_pergunta_id;
                  }
                  // Preservar proximo_bloco_id se existir (CRÍTICO para carregamento)
                  if (regra.proximo_bloco_id) {
                    regraNormalizada.proximo_bloco_id = regra.proximo_bloco_id;
                  }
                  return regraNormalizada;
                }).filter(regra => regra.resposta && regra.resposta.trim() !== '');
              } else {
                regrasNormalizadas = [];
              }
            }
            
            // Verificar se todas as opções estão marcadas para encerrar
            const todasEncerram = p.tipo_resposta === 'multipla_escolha' && regrasNormalizadas
              ? todasOpcoesEncerram(p.opcoes || [], regrasNormalizadas)
              : false;
            
            // Se todas as opções encerram, marcar como fim automaticamente
            const finalizaFluxo = todasEncerram || p.finaliza_fluxo === true;
            
            const perguntaNormalizada = {
              ...p,
              // Garantir que arrays vazios sejam preservados (indicam pergunta condicional sem regras)
              // Se o backend retornar null, converter para undefined para manter consistência
              regras_navegacao: regrasNormalizadas !== undefined ? regrasNormalizadas : (p.regras_navegacao === null ? undefined : p.regras_navegacao),
              finaliza_fluxo: finalizaFluxo
            };
            
            // Preservar proximo_bloco_id se existir (pode estar na pergunta marcadora)
            if (p.proximo_bloco_id) {
              perguntaNormalizada.proximo_bloco_id = p.proximo_bloco_id;
            }
            
            return perguntaNormalizada;
          })
        };
        
        return templateNormalizado;
      });
      
      setTemplates(templatesNormalizados);
    } catch (error) {
      console.error('Erro ao buscar templates:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os templates",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };


  const handleEdit = (template: Template) => {
    setEditingTemplate(template);

    // Função helper para verificar se todas as opções estão marcadas para encerrar
    const todasOpcoesEncerram = (opcoes: string[], regras: any[]): boolean => {
      const opcoesValidas = opcoes.map((opcao) => opcao.trim()).filter((opcao) => opcao !== '');
      if (opcoesValidas.length === 0) {
        return false;
      }
      
      // Verificar se todas as opções válidas têm regras que encerram (sem proxima_pergunta_id e sem proximo_bloco_id)
      return opcoesValidas.every((resposta) => {
        const regra = regras.find((r) => r.resposta === resposta);
        return regra && !regra.proxima_pergunta_id && !regra.proximo_bloco_id;
      });
    };

    // Primeiro, limpar vínculos inválidos das perguntas existentes
    const perguntasComVinculosLimpos = limparVinculosInvalidos(template.perguntas_formulario || []);

    // Normalizar perguntas_formulario para preservar arrays vazios que indicam fluxo condicional
    // IMPORTANTE: Preservar proximo_bloco_id nas regras de navegação
    const perguntasNormalizadas = perguntasComVinculosLimpos.map((p) => {
      
      // Normalizar regras de navegação preservando proximo_bloco_id
      let regrasNormalizadas = undefined;
      if (p.regras_navegacao !== undefined && p.regras_navegacao !== null) {
        if (Array.isArray(p.regras_navegacao)) {
          regrasNormalizadas = p.regras_navegacao.map(regra => {
            const regraNormalizada: any = {
              resposta: regra.resposta || ''
            };
            // Preservar proxima_pergunta_id se existir
            if (regra.proxima_pergunta_id) {
              regraNormalizada.proxima_pergunta_id = regra.proxima_pergunta_id;
            }
            // Preservar proximo_bloco_id se existir (CRÍTICO para edição)
            if (regra.proximo_bloco_id) {
              regraNormalizada.proximo_bloco_id = regra.proximo_bloco_id;
            }
            return regraNormalizada;
          }).filter(regra => regra.resposta && regra.resposta.trim() !== '');
        } else {
          regrasNormalizadas = [];
        }
      }
      
      // Verificar se todas as opções estão marcadas para encerrar
      const todasEncerram = p.tipo_resposta === 'multipla_escolha' && regrasNormalizadas
        ? todasOpcoesEncerram(p.opcoes || [], regrasNormalizadas)
        : false;
      
      // Se todas as opções encerram, marcar como fim automaticamente
      const finalizaFluxo = todasEncerram || p.finaliza_fluxo === true;
      
      const perguntaNormalizada = {
        ...p,
        // Garantir que arrays vazios sejam preservados (indicam pergunta condicional sem regras)
        // Se o backend retornar null, converter para undefined para manter consistência
        regras_navegacao: regrasNormalizadas !== undefined ? regrasNormalizadas : (p.regras_navegacao === null ? undefined : p.regras_navegacao),
        finaliza_fluxo: finalizaFluxo
      };
      
      // Preservar proximo_bloco_id se existir (pode estar na pergunta marcadora)
      if (p.proximo_bloco_id) {
        perguntaNormalizada.proximo_bloco_id = p.proximo_bloco_id;
      }
      
      return perguntaNormalizada;
    });
    
    setFormData({
      titulo: template.titulo,
      objetivo: template.objetivo || '',
      descricao: template.descricao || '',
      instrucoes: template.instrucoes || '',
      etapa_funil: template.etapa_funil,
      linha_produto: template.linha_produto || 'fotovoltaico',
      tipo_tarefa: template.tipo_tarefa,
      prazo_horas: template.prazo_horas,
      prioridade: template.prioridade,
      ativo: template.ativo,
      ordem: template.ordem ?? 1,
      perguntas_spin: template.perguntas_spin || [],
      perguntas_formulario: perguntasNormalizadas
    });
    // Definir dados originais para detectar mudanças não salvas
    setOriginalFormData({
      titulo: template.titulo,
      objetivo: template.objetivo || '',
      descricao: template.descricao || '',
      instrucoes: template.instrucoes || '',
      etapa_funil: template.etapa_funil,
      linha_produto: template.linha_produto || 'fotovoltaico',
      tipo_tarefa: template.tipo_tarefa,
      prazo_horas: template.prazo_horas,
      prioridade: template.prioridade,
      ativo: template.ativo,
      ordem: template.ordem ?? 1,
      perguntas_spin: template.perguntas_spin || [],
      perguntas_formulario: perguntasNormalizadas
    });
    // Mudar para a aba de edição
    setActiveTab('edit');
  };

  // Função para verificar se há alterações não salvas
  const hasUnsavedChanges = (): boolean => {
    if (!originalFormData) return false;

    // Comparar campos principais
    const fieldsToCompare = [
      'titulo', 'objetivo', 'descricao', 'instrucoes', 'etapa_funil', 'linha_produto',
      'tipo_tarefa', 'prioridade', 'prazo_horas', 'ordem', 'categoria', 'ativo'
    ];

    for (const field of fieldsToCompare) {
      if (formData[field as keyof Template] !== originalFormData[field as keyof Template]) {
        return true;
      }
    }

    // Comparar perguntas do formulário (comparar comprimento e conteúdo básico)
    const originalPerguntas = originalFormData.perguntas_formulario || [];
    const currentPerguntas = formData.perguntas_formulario || [];

    if (originalPerguntas.length !== currentPerguntas.length) {
      return true;
    }

    // Verificar se alguma pergunta foi modificada
    for (let i = 0; i < originalPerguntas.length; i++) {
      const originalPergunta = originalPerguntas[i];
      const currentPergunta = currentPerguntas[i];

      if (
        originalPergunta.texto !== currentPergunta.texto ||
        originalPergunta.tipo_resposta !== currentPergunta.tipo_resposta ||
        originalPergunta.finaliza_fluxo !== currentPergunta.finaliza_fluxo ||
        JSON.stringify(originalPergunta.opcoes || []) !== JSON.stringify(currentPergunta.opcoes || [])
      ) {
        return true;
      }
    }

    return false;
  };

  // Função para lidar com tentativas de fechamento quando há alterações não salvas
  const handleCloseAttempt = (closeAction: () => void) => {
    if (hasUnsavedChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowUnsavedChangesDialog(true);
    } else {
      closeAction();
    }
  };

  // Função para confirmar salvar alterações
  const handleConfirmSaveChanges = async () => {
    try {
      await handleSave();
      setShowUnsavedChangesDialog(false);
      if (pendingCloseAction) {
        pendingCloseAction();
      }
    } catch (error) {
      // Erro já tratado na handleSave
    }
  };

  // Função para descartar alterações
  const handleDiscardChanges = () => {
    setShowUnsavedChangesDialog(false);
    if (pendingCloseAction) {
      pendingCloseAction();
    }
  };

  // Função para cancelar o fechamento (continuar editando)
  const handleCancelClose = () => {
    setShowUnsavedChangesDialog(false);
    setPendingCloseAction(null);
  };

  // Função para resetar o formulário após salvar (sem verificar alterações não salvas)
  const resetFormAfterSave = () => {
    setEditingTemplate(null);
    setOriginalFormData(null);
    setFormData({
      titulo: '',
      objetivo: '',
      descricao: '',
      instrucoes: '',
      etapa_funil: '',
      linha_produto: 'fotovoltaico',
      tipo_tarefa: 'acao',
      prazo_horas: undefined,
      prioridade: 'media',
      ativo: true,
      ordem: 1,
      perguntas_spin: [],
      perguntas_formulario: []
    });
    // Voltar para a aba de lista
    setActiveTab('list');
  };

  const handleCancel = () => {
    const closeAction = () => {
      setEditingTemplate(null);
      setOriginalFormData(null);
      setFormData({
        titulo: '',
        objetivo: '',
        descricao: '',
        instrucoes: '',
        etapa_funil: '',
        linha_produto: 'fotovoltaico',
        tipo_tarefa: 'acao',
        prazo_horas: undefined,
        prioridade: 'media',
        ativo: true,
        ordem: 1,
        perguntas_spin: [],
        perguntas_formulario: []
      });
      // Voltar para a aba de lista
      setActiveTab('list');
    };

    handleCloseAttempt(closeAction);
  };

  const handleSave = async () => {
    try {
      if (!formData.titulo || !formData.etapa_funil || !formData.tipo_tarefa || !formData.prioridade || !formData.prazo_horas || formData.prazo_horas < 1) {
        toast({
          title: "Erro",
          description: "Preencha todos os campos obrigatórios, incluindo o prazo em horas (mínimo 1 hora)",
          variant: "destructive"
        });
        return;
      }

      // Validar ordem: não pode haver dois templates da mesma etapa com a mesma ordem
      if (formData.etapa_funil && formData.ordem) {
        // Buscar templates existentes para validar conflito
        const responseTemplates = await fetchWithAuth('/api/tarefas-templates');
        if (responseTemplates.ok) {
          const templatesExistentes: Template[] = await responseTemplates.json();
          const templateConflitante = templatesExistentes.find((t: Template) => 
            t.etapa_funil === formData.etapa_funil && 
            t.ordem === formData.ordem &&
            (t.linha_produto || 'fotovoltaico') === (formData.linha_produto || 'fotovoltaico') &&
            (!editingTemplate || t.id !== editingTemplate.id)
          );

          if (templateConflitante) {
            const etapaNome = etapasFunil.find(e => e.value === formData.etapa_funil)?.label || formData.etapa_funil;
            toast({
              title: "Erro",
              description: `Já existe um template na etapa "${etapaNome}" com ordem ${formData.ordem}. Escolha uma ordem diferente.`,
              variant: "destructive"
            });
            return;
          }
        }
      }

      // Normalizar perguntas_formulario para preservar arrays vazios que indicam fluxo condicional
      // IMPORTANTE: Preservar proximo_bloco_id nas regras de navegação
      const perguntasNormalizadas = (formData.perguntas_formulario || []).map((p) => {
        // Normalizar regras de navegação preservando proximo_bloco_id
        let regrasNormalizadas = undefined;
        if (p.regras_navegacao !== undefined) {
          if (Array.isArray(p.regras_navegacao)) {
            regrasNormalizadas = p.regras_navegacao.map(regra => {
              const regraNormalizada: any = {
                resposta: regra.resposta || ''
              };
              // Preservar proxima_pergunta_id se existir
              if (regra.proxima_pergunta_id) {
                regraNormalizada.proxima_pergunta_id = regra.proxima_pergunta_id;
              }
              // Preservar proximo_bloco_id se existir (CRÍTICO para salvamento)
              if (regra.proximo_bloco_id) {
                regraNormalizada.proximo_bloco_id = regra.proximo_bloco_id;
              }
              return regraNormalizada;
            }).filter(regra => regra.resposta && regra.resposta.trim() !== '');
          } else {
            regrasNormalizadas = [];
          }
        }
        
        const perguntaNormalizada: any = {
          ...p
        };
        
        // Preservar regras_navegacao
        if (regrasNormalizadas !== undefined) {
          perguntaNormalizada.regras_navegacao = regrasNormalizadas;
        }
        
        // Preservar proximo_bloco_id se existir (pode estar na pergunta marcadora)
        if (p.proximo_bloco_id) {
          perguntaNormalizada.proximo_bloco_id = p.proximo_bloco_id;
        }
        
        return perguntaNormalizada;
      });
      
      const dadosParaSalvar = {
        ...formData,
        linha_produto: formData.linha_produto || 'fotovoltaico',
        perguntas_formulario: perguntasNormalizadas
      };

      const url = editingTemplate 
        ? `/api/tarefas-templates/${editingTemplate.id}`
        : '/api/tarefas-templates';

      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dadosParaSalvar)
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Erro ao salvar template:', error);
        
        // Tratar erro específico de conflito de ordem
        if (error.error && (
          error.error.includes('uk_tarefas_templates_etapa_ordem') ||
          error.error.includes('Já existe um template')
        )) {
          throw new Error(error.error);
        }
        
        throw new Error(error.error || 'Erro ao salvar template');
      }

      const responseData = await response.json();

      toast({
        title: "Sucesso",
        description: editingTemplate ? "Template atualizado com sucesso" : "Template criado com sucesso"
      });

      // Resetar formulário sem verificar alterações não salvas
      resetFormAfterSave();
      fetchTemplates();
    } catch (error: any) {
      console.error('Erro ao salvar template:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível salvar o template",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este template?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/tarefas-templates/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar template');
      }

      toast({
        title: "Sucesso",
        description: "Template deletado com sucesso"
      });

      fetchTemplates();
    } catch (error) {
      console.error('Erro ao deletar template:', error);
      toast({
        title: "Erro",
        description: "Não foi possível deletar o template",
        variant: "destructive"
      });
    }
  };

  const handleDuplicate = async (id: string) => {
    if (!confirm('Tem certeza que deseja duplicar este template? Um novo template será criado com o nome "(copia)".')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/tarefas-templates/${id}/duplicate`, {
        method: 'POST'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao duplicar template');
      }

      toast({
        title: "Sucesso",
        description: "Template duplicado com sucesso"
      });

      fetchTemplates();
    } catch (error: any) {
      console.error('Erro ao duplicar template:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível duplicar o template",
        variant: "destructive"
      });
    }
  };

  const filteredTemplates = templates.filter((t) => {
    if (filterEtapa !== 'all' && t.etapa_funil !== filterEtapa) return false;
    if (filterLinha !== 'all' && (t.linha_produto || 'fotovoltaico') !== filterLinha) return false;
    return true;
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    if (!sortColumn) return 0;

    let aVal: string | number;
    let bVal: string | number;

    switch (sortColumn) {
      case 'titulo':
        aVal = a.titulo || '';
        bVal = b.titulo || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'etapa_funil':
        aVal = a.etapa_funil || '';
        bVal = b.etapa_funil || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'linha_produto':
        aVal = a.linha_produto || 'fotovoltaico';
        bVal = b.linha_produto || 'fotovoltaico';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'ordem':
        aVal = a.ordem ?? 0;
        bVal = b.ordem ?? 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      case 'prioridade':
        const prioridadeOrder = { 'urgente': 4, 'alta': 3, 'media': 2, 'baixa': 1 };
        aVal = prioridadeOrder[a.prioridade] || 0;
        bVal = prioridadeOrder[b.prioridade] || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      case 'ativo':
        aVal = a.ativo ? 1 : 0;
        bVal = b.ativo ? 1 : 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      default:
        return 0;
    }
  });

  const getEtapaNome = (etapa: string) => {
    const etapaObj = etapasFunil.find(e => e.value === etapa);
    return etapaObj?.label || etapa;
  };

  const getPrioridadeBadge = (prioridade: string) => {
    switch (prioridade) {
      case 'urgente':
        return <Badge className="bg-red-600 text-white">Urgente</Badge>;
      case 'alta':
        return <Badge className="bg-orange-500 text-white">Alta</Badge>;
      case 'media':
        return <Badge className="bg-yellow-500 text-white">Média</Badge>;
      case 'baixa':
        return <Badge variant="secondary">Baixa</Badge>;
      default:
        return <Badge variant="secondary">{prioridade}</Badge>;
    }
  };

  const handleRowClick = (template: Template) => {
    setSelectedTemplate(template);
    setShowDetailsModal(true);
  };

  const getTipoTarefaLabel = (tipo: string) => {
    switch (tipo) {
      case 'acao':
        return 'Ação';
      case 'pergunta':
        return 'Pergunta';
      case 'checklist':
        return 'Checklist';
      default:
        return tipo;
    }
  };

  const obterVinculoAnterior = (
    perguntaId: string,
    perguntas: PerguntaFormulario[]
  ): { pergunta: PerguntaFormulario; tipo: 'multipla_escolha' | 'discursiva'; respostas: string[] } | null => {
    for (const pergunta of perguntas) {
      const regras = Array.isArray(pergunta.regras_navegacao) ? pergunta.regras_navegacao : [];
      const respostasVinculadas = regras
        .filter((regra) => regra.proxima_pergunta_id === perguntaId && regra.resposta)
        .map((regra) => regra.resposta!.trim())
        .filter((resposta) => resposta !== '');

      if (respostasVinculadas.length > 0 && pergunta.tipo_resposta === 'multipla_escolha') {
        return {
          pergunta,
          tipo: 'multipla_escolha',
          respostas: respostasVinculadas
        };
      }

      if (pergunta.proxima_pergunta_id === perguntaId) {
        return {
          pergunta,
          tipo: pergunta.tipo_resposta === 'multipla_escolha' ? 'multipla_escolha' : 'discursiva',
          respostas: []
        };
      }
    }

    return null;
  };

  // Funções para gerenciar perguntas do formulário foram movidas para TemplateForm e PerguntaFormularioEditor

  const getPerguntaLabel = (perguntaId: string, perguntasBase?: PerguntaFormulario[]) => {
    if (!perguntaId) return 'Encerrar formulário';
    const perguntas = perguntasBase || (formData.perguntas_formulario || [])
      .slice()
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    const index = perguntas.findIndex((p) => p.id === perguntaId);
    const pergunta = perguntas.find((p) => p.id === perguntaId);
    if (!pergunta) {
      return 'Pergunta removida';
    }
    return index >= 0
      ? `Pergunta ${index + 1}: ${pergunta.texto}`
      : pergunta.texto;
  };

  // Função para lidar com fechamento do modal principal
  const handleModalClose = (newOpen: boolean) => {
    if (!newOpen) {
      const closeAction = () => {
        setEditingTemplate(null);
        setOriginalFormData(null);
        setFormData({
          titulo: '',
          objetivo: '',
          descricao: '',
          instrucoes: '',
          etapa_funil: '',
          linha_produto: 'fotovoltaico',
          tipo_tarefa: 'acao',
          prazo_horas: undefined,
          prioridade: 'media',
          ativo: true,
          ordem: 1,
          perguntas_spin: [],
          perguntas_formulario: []
        });
        setActiveTab('list');
        onOpenChange(false);
      };

      handleCloseAttempt(closeAction);
    } else {
      onOpenChange(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Settings className="h-6 w-6 text-primary" />
            Gerenciar Templates de Tarefas
          </DialogTitle>
          <DialogDescription>
            Crie, edite e gerencie templates de tarefas para o processo de vendas
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col min-h-0 flex-1">
          <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
            <TabsTrigger value="list">
              <FileText className="h-4 w-4 mr-2" />
              Lista de Templates
            </TabsTrigger>
            <TabsTrigger value={editingTemplate ? "edit" : "create"}>
              {editingTemplate ? (
                <>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar Template
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Template
                </>
              )}
            </TabsTrigger>
            <TabsTrigger value="blocos">
              <Settings className="h-4 w-4 mr-2" />
              Blocos de Perguntas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="blocos" className="space-y-4">
            <BlocosPerguntasTab />
          </TabsContent>

          <TabsContent value="list" className="space-y-4 overflow-auto flex-1 min-h-0 mt-4 max-h-[calc(90vh-14rem)]">
            {/* Filtros */}
            <Card>
              <CardHeader>
                <CardTitle>Filtros</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label>Etapa do Funil</Label>
                    <Select value={filterEtapa} onValueChange={setFilterEtapa}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas as etapas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as etapas</SelectItem>
                        {etapasFunil.map(etapa => (
                          <SelectItem key={etapa.value} value={etapa.value}>
                            {etapa.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Linha de produto</Label>
                    <Select value={filterLinha} onValueChange={setFilterLinha}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as linhas</SelectItem>
                        <SelectItem value="fotovoltaico">{LINHA_PRODUTO_TEMPLATE_LABELS.fotovoltaico}</SelectItem>
                        <SelectItem value="carregador_veicular">{LINHA_PRODUTO_TEMPLATE_LABELS.carregador_veicular}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lista de Templates */}
            <Card>
              <CardHeader>
                <CardTitle>Templates ({filteredTemplates.length})</CardTitle>
                <CardDescription>
                  Clique nos cabeçalhos das colunas para ordenar. Clique em uma linha para ver detalhes.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Carregando templates...
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum template encontrado
                  </div>
                ) : (
                  <div className="rounded-md border overflow-auto min-h-[360px] max-h-[520px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b-2 bg-muted/80 hover:bg-muted/80 sticky top-0 z-10 [&>th]:bg-muted/95 [&>th]:backdrop-blur-sm [&>th]:shadow-[0_1px_0_0_hsl(var(--border))]">
                          <TableHead
                            className={`min-w-[200px] whitespace-nowrap font-semibold text-foreground cursor-pointer select-none ${sortColumn === 'titulo' ? 'text-primary' : ''}`}
                            onClick={() => handleSort('titulo')}
                          >
                            Título
                            {sortColumn === 'titulo' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline opacity-60" />
                            )}
                          </TableHead>
                          <TableHead
                            className={`min-w-[140px] whitespace-nowrap font-semibold text-foreground cursor-pointer select-none ${sortColumn === 'etapa_funil' ? 'text-primary' : ''}`}
                            onClick={() => handleSort('etapa_funil')}
                          >
                            Etapa
                            {sortColumn === 'etapa_funil' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline opacity-60" />
                            )}
                          </TableHead>
                          <TableHead
                            className={`min-w-[120px] whitespace-nowrap font-semibold text-foreground cursor-pointer select-none ${sortColumn === 'linha_produto' ? 'text-primary' : ''}`}
                            onClick={() => handleSort('linha_produto')}
                          >
                            Linha
                            {sortColumn === 'linha_produto' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline opacity-60" />
                            )}
                          </TableHead>
                          <TableHead
                            className={`w-20 whitespace-nowrap font-semibold text-foreground cursor-pointer select-none text-center ${sortColumn === 'ordem' ? 'text-primary' : ''}`}
                            onClick={() => handleSort('ordem')}
                          >
                            Ordem
                            {sortColumn === 'ordem' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline opacity-60" />
                            )}
                          </TableHead>
                          <TableHead
                            className={`min-w-[100px] whitespace-nowrap font-semibold text-foreground cursor-pointer select-none ${sortColumn === 'prioridade' ? 'text-primary' : ''}`}
                            onClick={() => handleSort('prioridade')}
                          >
                            Prioridade
                            {sortColumn === 'prioridade' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline opacity-60" />
                            )}
                          </TableHead>
                          <TableHead
                            className={`min-w-[90px] whitespace-nowrap font-semibold text-foreground cursor-pointer select-none ${sortColumn === 'ativo' ? 'text-primary' : ''}`}
                            onClick={() => handleSort('ativo')}
                          >
                            Status
                            {sortColumn === 'ativo' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline opacity-60" />
                            )}
                          </TableHead>
                          <TableHead className="w-[140px] whitespace-nowrap font-semibold text-foreground bg-muted/80">
                            Ações
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedTemplates.map((template, index) => (
                          <TableRow
                            key={template.id}
                            onClick={() => handleRowClick(template)}
                            className={`cursor-pointer transition-colors ${index % 2 === 1 ? 'bg-muted/20' : ''}`}
                            title="Clique para ver detalhes"
                          >
                            <TableCell className="font-medium min-w-[200px] py-3">{template.titulo}</TableCell>
                            <TableCell className="min-w-[140px] py-3">
                              <Badge variant="outline">{getEtapaNome(template.etapa_funil)}</Badge>
                            </TableCell>
                            <TableCell className="min-w-[120px] py-3">
                              <Badge variant="secondary">
                                {LINHA_PRODUTO_TEMPLATE_LABELS[template.linha_produto || 'fotovoltaico']}
                              </Badge>
                            </TableCell>
                            <TableCell className="w-20 text-center py-3 tabular-nums">
                              {template.ordem ?? 1}
                            </TableCell>
                            <TableCell className="min-w-[100px] py-3">{getPrioridadeBadge(template.prioridade)}</TableCell>
                            <TableCell className="min-w-[90px] py-3">
                              {template.ativo ? (
                                <Badge className="bg-green-500 text-white">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Ativo
                                </Badge>
                              ) : (
                                <Badge variant="secondary">Inativo</Badge>
                              )}
                            </TableCell>
                            <TableCell className="w-[140px] py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleEdit(template);
                                  }}
                                  title="Editar template"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDuplicate(template.id);
                                  }}
                                  title="Duplicar template"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDelete(template.id);
                                  }}
                                  title="Excluir template"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value={editingTemplate ? "edit" : "create"} className="space-y-4">
            <TemplateForm
              editingTemplate={editingTemplate}
              formData={formData}
              onFormDataChange={setFormData}
              onSave={handleSave}
              onCancel={handleCancel}
              obterVinculoAnterior={obterVinculoAnterior}
              getPerguntaLabel={getPerguntaLabel}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Modal de Detalhes do Template */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Info className="h-6 w-6 text-primary" />
              Detalhes do Template
            </DialogTitle>
            <DialogDescription>
              Informações completas sobre o template de tarefa
            </DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-6">
                {/* Informações Principais */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Informações Principais
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-sm font-semibold text-muted-foreground">Título</Label>
                      <p className="text-lg font-medium mt-1">{selectedTemplate.titulo}</p>
                    </div>

                    {selectedTemplate.objetivo && (
                      <div>
                        <Label className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                          <Target className="h-4 w-4" />
                          Objetivo
                        </Label>
                        <p className="mt-1 text-sm whitespace-pre-wrap">{selectedTemplate.objetivo}</p>
                      </div>
                    )}

                    {selectedTemplate.descricao && (
                      <div>
                        <Label className="text-sm font-semibold text-muted-foreground">Descrição</Label>
                        <p className="mt-1 text-sm whitespace-pre-wrap">{selectedTemplate.descricao}</p>
                      </div>
                    )}

                    {selectedTemplate.instrucoes && (
                      <div>
                        <Label className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                          <Info className="h-4 w-4" />
                          Instruções Detalhadas
                        </Label>
                        <div className="mt-1 p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm whitespace-pre-wrap">{selectedTemplate.instrucoes}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Configurações da Tarefa */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="h-5 w-5" />
                      Configurações da Tarefa
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                          <Tag className="h-4 w-4" />
                          Etapa do Funil
                        </Label>
                        <div className="mt-1">
                          <Badge variant="outline" className="text-sm">
                            {getEtapaNome(selectedTemplate.etapa_funil)}
                          </Badge>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                          <CheckSquare className="h-4 w-4" />
                          Tipo de Tarefa
                        </Label>
                        <div className="mt-1">
                          <Badge variant="outline" className="text-sm">
                            {getTipoTarefaLabel(selectedTemplate.tipo_tarefa)}
                          </Badge>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Prioridade
                        </Label>
                        <div className="mt-1">
                          {getPrioridadeBadge(selectedTemplate.prioridade)}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Prazo
                        </Label>
                        <p className="mt-1 text-sm">
                          {selectedTemplate.prazo_horas 
                            ? `${selectedTemplate.prazo_horas} ${selectedTemplate.prazo_horas === 1 ? 'hora' : 'horas'}`
                            : 'Não definido'}
                        </p>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                          <GripVertical className="h-4 w-4" />
                          Ordem
                        </Label>
                        <p className="mt-1 text-sm">
                          {selectedTemplate.ordem ?? 1}
                        </p>
                      </div>

                      {/* Campo categoria removido - não existe na interface Template */}


                      <div>
                        <Label className="text-sm font-semibold text-muted-foreground">Status</Label>
                        <div className="mt-1">
                          {selectedTemplate.ativo ? (
                            <Badge className="bg-green-500 text-white">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Ativo
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inativo</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Perguntas do Formulário */}
                {(selectedTemplate.perguntas_formulario && selectedTemplate.perguntas_formulario.length > 0) ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Perguntas do Formulário
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedTemplate.perguntas_formulario
                          ?.slice()
                          .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                          .map((pergunta, index) => {

                            const tipoRespostaLabel =
                              pergunta.tipo_resposta === 'multipla_escolha'
                                ? 'Múltipla Escolha'
                                : 'Discursiva';

                            return (
                              <div key={pergunta.id} className="p-4 bg-muted/50 rounded-lg border">
                                <div className="flex flex-col gap-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant="outline" className="text-xs font-semibold min-w-[2rem] text-center">
                                      {index + 1}
                                    </Badge>
                                    <span className="font-medium">{pergunta.texto}</span>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 ml-9 text-xs">
                                    <Badge variant="outline">{tipoRespostaLabel}</Badge>
                                    {pergunta.finaliza_fluxo && (
                                      <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">
                                        Fim
                                      </Badge>
                                    )}
                                    {pergunta.finaliza_fluxo && (
                                      <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">
                                        Fim
                                      </Badge>
                                    )}
                                  </div>
                                  {pergunta.tipo_resposta === 'multipla_escolha' && pergunta.opcoes?.length ? (
                                    <div className="ml-9">
                                      <p className="text-xs text-muted-foreground mb-1">Opções:</p>
                                      <ul className="list-disc list-inside text-sm text-muted-foreground">
                                        {pergunta.opcoes.map((opcao, idx) => (
                                          <li key={idx}>{opcao}</li>
                                        ))}
                                      </ul>
                                      {Array.isArray(pergunta.regras_navegacao) && pergunta.regras_navegacao.length > 0 && (
                                        <div className="mt-2">
                                          <p className="text-xs text-muted-foreground mb-1">Encaminhamentos:</p>
                                          <ul className="text-xs text-muted-foreground space-y-1">
                                            {pergunta.regras_navegacao.map((regra, regraIdx) => {
                                              const blocoVinculado = regra.proximo_bloco_id 
                                                ? blocosDisponiveis.find(b => b.id === regra.proximo_bloco_id)
                                                : null;
                                              return (
                                                <li key={`${pergunta.id}-detalhe-regra-${regraIdx}`}>
                                                  <span className="font-semibold text-foreground">{regra.resposta || `Opção ${regraIdx + 1}`}</span>
                                                  <span className="mx-1 text-muted-foreground">→</span>
                                                  <span>
                                                    {regra.proxima_pergunta_id
                                                      ? getPerguntaLabel(regra.proxima_pergunta_id, selectedTemplate.perguntas_formulario || [])
                                                      : regra.proximo_bloco_id && blocoVinculado
                                                      ? `${blocoVinculado.nome} (bloco)`
                                                      : 'Encerrar formulário'}
                                                  </span>
                                                </li>
                                              );
                                            })}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  ) : null}
                                  {/* Exibir encaminhamento para perguntas discursivas vinculadas a blocos */}
                                  {pergunta.tipo_resposta === 'discursiva' && pergunta.proximo_bloco_id && (
                                    <div className="ml-9 mt-2">
                                      <div className="mt-2">
                                        <p className="text-xs text-muted-foreground mb-1">Encaminhamento:</p>
                                        <ul className="text-xs text-muted-foreground space-y-1">
                                          <li>
                                            <span className="font-semibold text-foreground">Pergunta discursiva</span>
                                            <span className="mx-1 text-muted-foreground">→</span>
                                            <span>
                                              {(() => {
                                                const blocoVinculado = blocosDisponiveis.find(b => b.id === pergunta.proximo_bloco_id);
                                                return blocoVinculado 
                                                  ? `${blocoVinculado.nome} (bloco)`
                                                  : 'Bloco';
                                              })()}
                                            </span>
                                          </li>
                                        </ul>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Perguntas do Formulário
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-6 text-muted-foreground">
                        Nenhuma pergunta configurada para este template.
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Conclusões Automáticas */}
                {selectedTemplate.conclusoes_automaticas && selectedTemplate.conclusoes_automaticas.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        Conclusões Automáticas
                      </CardTitle>
                      <CardDescription>
                        Conclusões definidas baseadas nas respostas das perguntas de fim
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedTemplate.conclusoes_automaticas.map((conclusao) => {
                          // Obter as perguntas relacionadas a esta conclusão
                          const perguntasConclusao = conclusao.pergunta_ids
                            .map(id => selectedTemplate.perguntas_formulario?.find(p => p.id === id))
                            .filter(Boolean) as PerguntaFormulario[];

                          return (
                            <div key={conclusao.id} className="p-4 bg-muted/50 rounded-lg border">
                              <div className="flex items-start gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="outline" className="text-sm font-semibold">
                                      {conclusao.nome}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      {conclusao.pergunta_ids.length} pergunta(s)
                                    </span>
                                  </div>
                                  {perguntasConclusao.length > 0 && (
                                    <div className="ml-2 mt-2">
                                      <p className="text-xs text-muted-foreground mb-1">Perguntas que levam a esta conclusão:</p>
                                      <div className="flex flex-wrap gap-2">
                                        {perguntasConclusao.map((pergunta) => (
                                          <Badge
                                            key={pergunta.id}
                                            variant="secondary"
                                            className="text-xs"
                                          >
                                            {getPerguntaLabel(pergunta.id, selectedTemplate.perguntas_formulario || [])}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  selectedTemplate.perguntas_formulario && selectedTemplate.perguntas_formulario.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5" />
                          Conclusões Automáticas
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-center py-6 text-muted-foreground">
                          Nenhuma conclusão automática configurada para este template.
                        </div>
                      </CardContent>
                    </Card>
                  )
                )}

                {/* Ações */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedTemplate(null);
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Fechar
                  </Button>
                  <Button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleEdit(selectedTemplate);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar Template
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação para alterações não salvas */}
      <Dialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <DialogContent className="max-w-2xl w-[calc(100%-2rem)] sm:w-full">
          <DialogHeader className="pr-8">
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
              Alterações não salvas
            </DialogTitle>
            <DialogDescription className="text-base break-words">
              Você fez alterações no template que ainda não foram salvas. O que deseja fazer?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-end sm:gap-3">
            <Button variant="outline" onClick={handleCancelClose} className="w-full sm:w-auto min-w-[140px]">
              <X className="h-4 w-4 mr-2" />
              Continuar Editando
            </Button>
            <Button variant="outline" onClick={handleDiscardChanges} className="w-full sm:w-auto min-w-[140px]">
              <Trash2 className="h-4 w-4 mr-2" />
              Descartar Alterações
            </Button>
            <Button onClick={handleConfirmSaveChanges} className="w-full sm:w-auto min-w-[140px]">
              <Save className="h-4 w-4 mr-2" />
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

